'use client'

import { useState, useRef, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, X, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { StatusIndicator } from "@/components/StatusIndicator";
import Image from "next/image";

interface FeatureCard {
  title: string;
  description: string;
  metrics: {
    label: string;
    value: string;
    unit: string;
    status: "OPTIMAL" | "GOOD" | "FAIR";
  }[];
  insight: string;
  category: string;
  image: string;
  caseStudyUrl: string;
}

const features: FeatureCard[] = [
  {
    title: "Harold's Chicken",
    description: "Developed a modern, responsive website for Harold's Chicken franchise location that showcases their menu and locations. The site features a clean, user-friendly design that effectively represents their brand.",
    metrics: [],
    insight: "",
    category: "Web Development",
    image: "/case-studies/H1.jpg",
    caseStudyUrl: "/case-studies/harolds-chicken"
  },
  {
    title: "Casa Grande",
    description: "Created a custom website for this family-owned Mexican restaurant featuring an intuitive online ordering system, real-time menu updates, and mobile-first design. Implemented secure payment processing and order tracking to enhance customer experience.",
    metrics: [
      {
        label: "Order Value",
        value: "+15",
        unit: "%",
        status: "OPTIMAL"
      },
      {
        label: "Monthly Orders",
        value: "215",
        unit: "+",
        status: "GOOD"
      }
    ],
    insight: "New website reduced phone orders by 30% and increased average order value",
    category: "Web Development",
    image: "/case-studies/Casa.jpg",
    caseStudyUrl: "/case-studies/casa-grande"
  },
  {
    title: "University of Dubuque",
    description: "Sports photography coverage showcasing game day action shots, team photos, and highlights. Delivers professional quality images suitable for social media, marketing materials, and team archives.",
    metrics: [
      {
        label: "Photos Per Game",
        value: "150-200",
        unit: "photos",
        status: "OPTIMAL"
      },
      {
        label: "Engagement",
        value: "+18",
        unit: "%",
        status: "GOOD"
      }
    ],
    insight: "Professional sports photography enhances team visibility and engagement",
    category: "Sports Photography",
    image: "/case-studies/UD.jpg",
    caseStudyUrl: "/case-studies/university-of-dubuque"
  },
  {
    title: "Lonnie Ray's",
    description: "Delivered a complete digital presence for this youth basketball program, combining professional photography with a custom-built website. The site features player profiles, schedule management, and a gallery showcasing game highlights.",
    metrics: [
      {
        label: "Site Visitors",
        value: "800",
        unit: "monthly",
        status: "OPTIMAL"
      },
      {
        label: "Photo Downloads",
        value: "250",
        unit: "monthly",
        status: "OPTIMAL"
      }
    ],
    insight: "Website and photo content helped increase program visibility and parent engagement",
    category: "Web & Photography",
    image: "/case-studies/Lonnie.jpg",
    caseStudyUrl: "/case-studies/lonnie-rays"
  },
  {
    title: "Kutn Kings",
    description: "Designed a distinctive logo for this modern barbershop that captures their premium yet approachable brand identity. The design system includes variations for different applications and social media integration.",
    metrics: [
      {
        label: "Client Growth",
        value: "+15",
        unit: "%",
        status: "OPTIMAL"
      },
      {
        label: "Social Growth",
        value: "850",
        unit: "followers",
        status: "GOOD"
      }
    ],
    insight: "New logo design helped establish professional presence on social media",
    category: "Logo Design",
    image: "/case-studies/Kutn.jpg",
    caseStudyUrl: "/case-studies/kutn-kings"
  }
];

export function FeatureCards() {
  const [openCardIndex, setOpenCardIndex] = useState<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      
      // Calculate current index based on scroll position
      // Dynamically get card width from first card element
      const firstCard = container.children[0] as HTMLElement;
      if (firstCard) {
        const cardWidth = firstCard.offsetWidth;
        const gap = window.innerWidth >= 768 ? 24 : 16; // md:gap-6 (24px) or gap-4 (16px)
        const newIndex = Math.round(container.scrollLeft / (cardWidth + gap));
        setCurrentIndex(Math.min(newIndex, features.length - 1));
      }
    }
  };


  const scrollToCard = (index: number) => {
    if (scrollContainerRef.current && openCardIndex === null) {
      const container = scrollContainerRef.current;
      
      // Dynamically get card width from first card element
      const firstCard = container.children[0] as HTMLElement;
      if (firstCard) {
        const cardWidth = firstCard.offsetWidth;
        const gap = window.innerWidth >= 768 ? 24 : 16; // md:gap-6 (24px) or gap-4 (16px)
        const scrollPosition = index * (cardWidth + gap);
        
        // For the last card, ensure it's fully visible
        const maxScroll = container.scrollWidth - container.clientWidth;
        const finalScrollPosition = Math.min(scrollPosition, maxScroll);
        
        container.scrollTo({
          left: finalScrollPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      checkScrollPosition();
      container.addEventListener('scroll', checkScrollPosition);
      window.addEventListener('resize', checkScrollPosition);

      return () => {
        container.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', checkScrollPosition);
      };
    }
  }, []);

  return (
    <section className="bg-white overflow-hidden pt-16 md:pt-24">
      <div className="mx-auto max-w-[110rem] px-4 sm:px-6 lg:px-10">

        <div 
          ref={scrollContainerRef}
          className={cn(
            "flex gap-4 md:gap-6 no-scrollbar",
            openCardIndex !== null 
              ? "overflow-x-hidden px-0" 
              : "overflow-x-auto snap-x snap-mandatory pl-4"
          )}
        >
          {features.map((feature, index) => (
            <Card
              key={index}
              className={cn(
                "relative h-[480px] md:h-[580px] rounded-2xl md:rounded-3xl overflow-hidden border-0",
                "transition-all duration-300 flex-none",
                openCardIndex === index
                  ? "w-full min-w-full"
                  : "w-[85vw] md:w-[1024px] snap-start"
              )}
            >
              <Image
                src={feature.image}
                alt={feature.title}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 85vw, (max-width: 1200px) 50vw, 33vw"
                priority={index === 0}
                loading={index === 0 ? "eager" : "lazy"}
                quality={85}
                placeholder="blur"
                blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4gHYSUNDX1BST0ZJTEUAAQEAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADb/2wBDABQODxIPDRQSEBIXFRQdHx4eHRoaHSQtJSEkLzYvLy0vLzYvLy8vLy8vLy8vLy8vLy8vLy8vLy8vLy8vLy8vLz/2wBDAR0dHh4eHRoaHSQtJSEkLzYvLy8vLy8vLy8vLy8vLy8vLy8vLy8vLy8vLy8vLy8vLy8vLy8vLy8vLy8vLy8vLz/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAb/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k="
              />
              <div className={cn(
                "absolute inset-0",
                openCardIndex === index 
                  ? "bg-gradient-to-b from-black/40 via-black/60 to-black/80"
                  : "bg-black/40"
              )} />
              <div className="relative h-full p-10 flex flex-col justify-between text-white">
                <div className="flex justify-between items-start">
                  <span className="text-sm md:text-base font-medium uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.8)', letterSpacing: '0.05em' }}>
                    {feature.category}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative w-8 h-8 md:w-10 md:h-10 rounded-full bg-white p-0 hover:bg-white/90"
                    onClick={() => {
                      if (openCardIndex === index) {
                        setOpenCardIndex(null);
                      } else {
                        setOpenCardIndex(index);
                        // Scroll to the card when expanding, with special handling for last card
                        setTimeout(() => {
                          if (index === features.length - 1) {
                            // For the last card, scroll to the end
                            if (scrollContainerRef.current) {
                              scrollContainerRef.current.scrollTo({
                                left: scrollContainerRef.current.scrollWidth,
                                behavior: 'smooth'
                              });
                            }
                          } else {
                            scrollToCard(index);
                          }
                        }, 50);
                      }
                    }}
                  >
                    {openCardIndex === index ? (
                      <X className="h-4 w-4 md:h-5 md:w-5 text-[#171A20]" />
                    ) : (
                      <Plus className="h-4 w-4 md:h-5 md:w-5 text-[#171A20]" />
                    )}
                  </Button>
                </div>

                <div className="mt-auto">
                  {openCardIndex === index ? (
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6 mb-4 md:mb-8">
                      <div className={index === 0 ? "md:col-span-5" : "md:col-span-3"}>
                        <div className={index === 0 ? "max-w-3xl" : ""}>
                          <h3 className="text-xl md:text-3xl font-semibold leading-tight mb-4 md:mb-4" style={{ color: 'rgb(255,255,255)' }}>
                            {feature.title}
                          </h3>
                          <p className="text-base md:text-lg leading-relaxed mb-4 md:mb-8" style={{ color: 'rgba(255,255,255,0.9)' }}>{feature.description}</p>
                            <Button asChild variant="secondary" className="group w-full md:w-auto px-8">
                              <a href={feature.caseStudyUrl} className="flex items-center justify-center md:justify-start gap-2">
                                View Case Study
                                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                              </a>
                            </Button>
                        </div>
                      </div>
                      {index !== 0 && (
                        <div className="md:col-span-2">
                          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 md:p-4">
                            <div className="space-y-4 md:space-y-4 mb-4 md:mb-4">
                              {feature.metrics.map((metric, mIndex) => (
                                <div key={mIndex} className="space-y-2">
                                  <p className="text-xs md:text-sm text-white/80">{metric.label}</p>
                                  <div className="flex items-center justify-between gap-4">
                                    <span className="text-lg md:text-xl font-semibold">
                                      {metric.value} {metric.unit}
                                    </span>
                                    <StatusIndicator status={metric.status} size="lg" />
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="pt-4 md:pt-4 border-t border-white/10">
                              <p className="text-xs md:text-sm">{feature.insight}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <h3 className="text-xl md:text-3xl font-semibold leading-tight" style={{ color: 'rgb(255,255,255)' }}>
                      {feature.title}
                    </h3>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
        

        {/* Dot Navigation */}
        <div className="flex justify-center items-center gap-2 mt-8 mb-8">
          {features.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToCard(index)}
              className={cn(
                "w-2 h-2 rounded-full transition-all duration-300",
                currentIndex === index
                  ? "bg-[#171A20] w-8"
                  : "bg-[#171A20]/30 hover:bg-[#171A20]/50"
              )}
            />
          ))}
        </div>
      </div>
    </section>
  );
}